import ApplicationController from './application_controller';

// The per-session data-point list under a goal chart, folded behind a twisty.
//
// The list is the audit trail — every measurement, its date, its value in the
// goal's own units, and the document it came from — so it has to stay
// reachable. But it is DETAIL, and unrolled it is the loudest thing on the
// panel: a goal worked weekly for two years puts a hundred rows under a 200px
// chart and pushes the trials table, the short-term goals and the timeline a
// screen and a half down. Collapsed, the trigger still carries the count,
// which is the useful part while it is shut — how many sessions are recorded
// against this goal, without unrolling any of them.
//
// The one exception is the graph being hidden. There the rows are not detail,
// they are the substitute for the chart, and collapsing both would leave the
// panel saying nothing at all — so the panel's "Hide graph" toggle opens the
// list, and showing the graph again puts it back the way it was, unless the
// reader touched the twisty in between (their click wins). The panel
// announces that with a `goal-graph-toggle:toggled` event; a mount point with
// no graph toggle simply never hears one and the disclosure works on its own.
//
// State lives on this element only: per goal, per page load, never persisted.
//
// Targets:
//   list   the rows (hidden while collapsed)
//   button the trigger (carries aria-expanded and the title)
//   caret  the caret that rotates open
export default class extends ApplicationController {
  static targets = ['list', 'button', 'caret'];
  static values = { open: { type: Boolean, default: false } };

  // A click on the twisty is the reader's own decision and outranks anything
  // we memorised for them: forget what the list looked like before the graph
  // was hidden, so putting the graph back cannot undo a choice they made in
  // the meantime.
  toggle() {
    this.openBeforeHide = undefined;
    this.openValue = !this.openValue;
  }

  // A goal-graph-toggle above us changed state. The panel is our ANCESTOR, so
  // its event never reaches us by bubbling and we listen on the window
  // instead — which means we also hear every other panel on the page. Only the
  // one that contains us is ours.
  graphToggled(event) {
    const panel = event.target;
    if (!panel || typeof panel.contains !== 'function' || !panel.contains(this.element)) return;

    if (event.detail.hidden) {
      if (this.openBeforeHide === undefined) this.openBeforeHide = this.openValue;
      this.openValue = true;
    } else if (this.openBeforeHide !== undefined) {
      this.openValue = this.openBeforeHide;
      this.openBeforeHide = undefined;
    }
  }

  openValueChanged() {
    const open = this.openValue;
    this.listTargets.forEach((element) => {
      element.hidden = !open;
    });
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute('aria-expanded', String(open));
      // No "behind this chart": a panel whose data cannot be charted shows
      // this list and nothing else, and the copy has to be true there too.
      this.buttonTarget.title = open ? 'Hide the individual sessions' : 'Show the individual sessions';
    }
    if (this.hasCaretTarget) {
      this.caretTarget.classList.toggle('is-open', open);
    }
  }
}
